import React from 'react';
import {View, Text} from 'react-native';
import {TFunction} from 'i18next';
import PasswordToggleButton from './PasswordToggleButton';

// ─────────────────────────────────────────────────────────────────────────────
// InputPasswordIcon
// ─────────────────────────────────────────────────────────────────────────────

interface InputPasswordIconProps {
  isPasswordVisible: boolean;
  onToggle: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  iconContainerStyle: any;
  openEyeIconColor: string;
  closeEyeIconColor: string;
  t: TFunction;
}

/**
 * Thin wrapper around `PasswordToggleButton` that absorbs the two
 * translation-key calls so the call-site in `Input.tsx` / `Input.web.tsx`
 * passes only 6 props instead of 8, eliminating the SonarQube-detected
 * duplicated JSX block between the two files.
 */
export const InputPasswordIcon: React.FC<InputPasswordIconProps> = ({
  isPasswordVisible,
  onToggle,
  iconContainerStyle,
  openEyeIconColor,
  closeEyeIconColor,
  t,
}) => (
  <PasswordToggleButton
    isPasswordVisible={isPasswordVisible}
    onPress={onToggle}
    style={iconContainerStyle}
    openEyeIconColor={openEyeIconColor}
    closeEyeIconColor={closeEyeIconColor}
    hidePasswordLabel={t('accessibility.hidePassword')}
    showPasswordLabel={t('accessibility.showPassword')}
  />
);

// ─────────────────────────────────────────────────────────────────────────────
// InputErrorText
// ─────────────────────────────────────────────────────────────────────────────

interface InputErrorTextProps {
  message: string | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errorTextStyle: any;
}

/**
 * Renders the live-region error / validation message for an Input field.
 * Extracted from both `Input.tsx` and `Input.web.tsx` to eliminate the
 * duplicated 5-line error block detected by SonarQube.
 */
export const InputErrorText: React.FC<InputErrorTextProps> = ({message, errorTextStyle}) => (
  <View accessibilityLiveRegion="polite">
    <Text style={errorTextStyle} role="alert">
      {message}
    </Text>
  </View>
);